import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getGuidedSessions, createGuidedSession, endGuidedSession, getSessionSteps, addSessionStep, completeSessionStep, extractErrorMessage } from '../services/api';
import { FaPlus, FaPlay, FaStop, FaCheck, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { useToast } from '../components/Toast';
import UserSelect from '../components/UserSelect';

function Sessions({ user }) {
  const toast = useToast();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showStepModal, setShowStepModal] = useState(false);
  const [savingCreate, setSavingCreate] = useState(false);
  const [formErrorCreate, setFormErrorCreate] = useState(null);
  const [savingStep, setSavingStep] = useState(false);
  const [formErrorStep, setFormErrorStep] = useState(null);
  const [expandedSession, setExpandedSession] = useState(null);
  const [sessionSteps, setSessionSteps] = useState({});
  const [newSession, setNewSession] = useState({
    user_id: '',
    type: 'guided',
    title: '',
    description: ''
  });
  const [newStep, setNewStep] = useState({
    session_id: '',
    instruction: '',
    duration: 5,
    order_num: 1
  });

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      const data = await getGuidedSessions();
      setSessions(data.sessions || data || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error(extractErrorMessage(error, 'Sessions konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const fetchSteps = async (sessionId) => {
    try {
      const data = await getSessionSteps(sessionId);
      setSessionSteps(prev => ({ ...prev, [sessionId]: data.steps || data || [] }));
    } catch (error) {
      console.error('Error fetching steps:', error);
      toast.error(extractErrorMessage(error, 'Schritte konnten nicht geladen werden'));
    }
  };

  const handleCreateSession = async (e) => {
    e.preventDefault();
    if (savingCreate) return;
    setSavingCreate(true);
    setFormErrorCreate(null);
    try {
      await createGuidedSession(newSession);
      setNewSession({ user_id: '', type: 'guided', title: '', description: '' });
      setShowCreateModal(false);
      toast.success('Session erstellt');
      fetchSessions();
    } catch (error) {
      console.error('Error creating session:', error);
      setFormErrorCreate(extractErrorMessage(error, 'Fehler beim Erstellen der Session'));
    } finally {
      setSavingCreate(false);
    }
  };

  const handleEndSession = async (id) => {
    if (!window.confirm('Session wirklich beenden?')) return;
    try {
      await endGuidedSession(id);
      toast.success('Session beendet');
      fetchSessions();
    } catch (error) {
      console.error('Error ending session:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Beenden der Session'));
    }
  };

  const handleAddStep = async (e) => {
    e.preventDefault();
    if (savingStep) return;
    setSavingStep(true);
    setFormErrorStep(null);
    try {
      const sessionId = newStep.session_id;
      await addSessionStep(sessionId, newStep);
      setNewStep({ session_id: '', instruction: '', duration: 5, order_num: 1 });
      setShowStepModal(false);
      toast.success('Schritt hinzugefügt');
      fetchSteps(sessionId);
    } catch (error) {
      console.error('Error adding step:', error);
      setFormErrorStep(extractErrorMessage(error, 'Fehler beim Hinzufügen des Schritts'));
    } finally {
      setSavingStep(false);
    }
  };

  const handleCompleteStep = async (sessionId, stepId) => {
    try {
      await completeSessionStep(sessionId, stepId);
      toast.success('Schritt abgeschlossen');
      fetchSteps(sessionId);
    } catch (error) {
      console.error('Error completing step:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Abschließen des Schritts'));
    }
  };

  const closeCreateModal = () => {
    if (savingCreate) return;
    setShowCreateModal(false);
    setFormErrorCreate(null);
  };

  const closeStepModal = () => {
    if (savingStep) return;
    setShowStepModal(false);
    setFormErrorStep(null);
  };

  const toggleSession = async (sessionId) => {
    if (expandedSession === sessionId) {
      setExpandedSession(null);
    } else {
      setExpandedSession(sessionId);
      if (!sessionSteps[sessionId]) {
        await fetchSteps(sessionId);
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      active: { label: 'Aktiv', className: 'badge badge-success' },
      completed: { label: 'Abgeschlossen', className: 'badge badge-info' },
      paused: { label: 'Pausiert', className: 'badge badge-warning' },
      cancelled: { label: 'Abgebrochen', className: 'badge badge-danger' }
    };
    const s = statusMap[status] || { label: status, className: 'badge' };
    return <span className={s.className}>{s.label}</span>;
  };

  const sessionTypes = ['guided', 'remote', 'timed', 'freestyle'];

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1>Geführte Sessions</h1>
          <button className="add-btn" onClick={() => setShowCreateModal(true)}>
            <FaPlus /> Neue Session
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Titel</th>
                  <th>Typ</th>
                  <th>Status</th>
                  <th>Beschreibung</th>
                  <th>Erstellt</th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {sessions.length > 0 ? sessions.map(session => (
                  <React.Fragment key={session.id}>
                    <tr className={expandedSession === session.id ? 'expanded-row' : ''}>
                      <td>
                        <button className="icon-btn" onClick={() => toggleSession(session.id)}>
                          {expandedSession === session.id ? <FaChevronDown /> : <FaChevronRight />}
                        </button>
                      </td>
                      <td><strong>{session.title}</strong></td>
                      <td><span className="badge">{session.type}</span></td>
                      <td>{getStatusBadge(session.status)}</td>
                      <td>{session.description || '-'}</td>
                      <td>{new Date(session.created_at || session.timestamp).toLocaleDateString('de-DE')}</td>
                      <td className="actions-cell">
                        {session.status === 'active' && (
                          <button className="icon-btn delete" onClick={() => handleEndSession(session.id)} title="Beenden">
                            <FaStop />
                          </button>
                        )}
                        <button className="icon-btn edit" onClick={() => { setNewStep({ ...newStep, session_id: session.id }); setShowStepModal(true); }} title="Schritt hinzufügen">
                          <FaPlus />
                        </button>
                      </td>
                    </tr>
                    {expandedSession === session.id && (
                      <tr className="sub-row">
                        <td colSpan="7">
                          <div className="sub-table-container">
                            <h4>Schritte</h4>
                            {(sessionSteps[session.id] || []).length > 0 ? (
                              <table className="data-table sub-table">
                                <thead>
                                  <tr>
                                    <th>#</th>
                                    <th>Anweisung</th>
                                    <th>Dauer (min)</th>
                                    <th>Status</th>
                                    <th>Aktion</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(sessionSteps[session.id] || []).map(step => (
                                    <tr key={step.id}>
                                      <td>{step.order_num}</td>
                                      <td>{step.instruction}</td>
                                      <td>{step.duration}</td>
                                      <td>{step.completed_at ? <span className="badge badge-success">Erledigt</span> : <span className="badge badge-warning">Offen</span>}</td>
                                      <td>
                                        {!step.completed_at && (
                                          <button className="icon-btn success" onClick={() => handleCompleteStep(session.id, step.id)} title="Abschließen">
                                            <FaCheck />
                                          </button>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <p className="no-data">Keine Schritte vorhanden</p>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )) : (
                  <tr><td colSpan="7" className="no-data">Keine Sessions gefunden</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {showCreateModal && (
          <div className="modal-overlay" onClick={closeCreateModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Neue Session</h2>
              {formErrorCreate && <div className="shared-form-error-banner">{formErrorCreate}</div>}
              <form onSubmit={handleCreateSession}>
                <div className="form-group">
                  <UserSelect
                    value={newSession.user_id}
                    onChange={id => setNewSession({...newSession, user_id: id})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Typ</label>
                  <select
                    value={newSession.type}
                    onChange={e => setNewSession({...newSession, type: e.target.value})}
                  >
                    {sessionTypes.map(t => (
                      <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Titel *</label>
                  <input
                    type="text"
                    value={newSession.title}
                    onChange={e => setNewSession({...newSession, title: e.target.value})}
                    placeholder="Session-Titel"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Beschreibung</label>
                  <textarea
                    value={newSession.description}
                    onChange={e => setNewSession({...newSession, description: e.target.value})}
                    placeholder="Beschreibung der Session..."
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeCreateModal} disabled={savingCreate}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingCreate}>
                    {savingCreate ? 'Speichert...' : 'Erstellen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showStepModal && (
          <div className="modal-overlay" onClick={closeStepModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Schritt hinzufügen</h2>
              {formErrorStep && <div className="shared-form-error-banner">{formErrorStep}</div>}
              <form onSubmit={handleAddStep}>
                <div className="form-group">
                  <label>Anweisung *</label>
                  <textarea
                    value={newStep.instruction}
                    onChange={e => setNewStep({...newStep, instruction: e.target.value})}
                    placeholder="Anweisung für diesen Schritt..."
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Dauer (Minuten)</label>
                  <input
                    type="number"
                    min="1"
                    max="120"
                    value={newStep.duration}
                    onChange={e => setNewStep({...newStep, duration: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div className="form-group">
                  <label>Reihenfolge</label>
                  <input
                    type="number"
                    min="1"
                    value={newStep.order_num}
                    onChange={e => setNewStep({...newStep, order_num: parseInt(e.target.value) || 1})}
                  />
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeStepModal} disabled={savingStep}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={savingStep}>
                    {savingStep ? 'Speichert...' : 'Hinzufügen'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Sessions;
